import React, { MouseEventHandler, useCallback, useMemo, useState, useEffect } from 'react';
import { IStepNavigation } from '.';
import Icon, { Icons } from '@components/Icon';
import useTranslations from '@hooks/useTranslations';
import { useTheme } from '@hooks/useTheme';
import { useMediaQuery } from '@hooks/useMediaquery';
import { useFeatureSwitch } from '@hooks/useFeatureSwitch';
import {
    commentTemplateId,
    mandatoryFeesTemplateId,
    optionalAccessoriesTemplateId,
    optionalFeesTemplateId,
    paymentTemplateId,
    pxTemplateId,
    registrationFeesTemplateId,
    scrappageTemplateId,
    userTemplateId,
} from '@components/FAQs';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { isBrandAC, isMarketBE, isMarketIT, ModalVersion } from '../../../constants/main';
import {
    hasMandatoryDealerFees,
    hasOptionalDealerFees,
    hasSelectedOptionalAccessories,
    hasSelectedOptionalDealerFees,
} from '@utils/Deal.utils';
import { hasMonthlyPriceByType } from '@utils/Price.utils';
import { EMPLOYEE_PRICE_TYPE } from '../../../services/filters/filters';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from '../../../context/featureSwitchApp';
import isEmpty from 'lodash/isEmpty';
import { useDispatch } from 'react-redux';
import UIDuck from '../../../redux/commonDucks/ui.duck';
import { useSsr } from '@hooks/useSsr';
import { useUserDuck } from '@hooks/useUserDuck';
import Link from '@components/Link';
import { useJourneyType } from '@hooks/useJourneyType';

interface GenerateButtonElementProps {
    condition: boolean;
    iconCondition: boolean;
    onClickFunction: MouseEventHandler<HTMLButtonElement>;
    icon: Icons;
    title: string;
    ref: React.RefObject<HTMLButtonElement>;
    dataTestId: string;
}

export const StepNavigationTemplate = ({ className }: IStepNavigation) => {
    const [dropdown, setDropdown] = useState<boolean>(false);
    const { t } = useTranslations();
    const { carDetails, currentDeal } = useCarDetailsDuck();
    const { isRent } = useJourneyType();
    const [blockNext, setBlockNext] = useState(false);
    const { isBrowser } = useSsr();
    const { isUserLoggedInAsDealer } = useUserDuck();

    const [isScrolled, setIsScrolled] = useState(false);

    const theme = useTheme();
    const lg = useMediaQuery(`(min-width: ${theme.flexboxgrid.breakpoints.lg}em)`);

    useEffect(() => {
        if (isBrowser) {
            const checkScrollPosition = () => {
                if (lg) {
                    const element = document.querySelector('.header-container, .header');
                    if (element) {
                        const rect = element.getBoundingClientRect();
                        const elemHeight = rect.height;
                        const scrollPos = window.pageYOffset || document.documentElement.scrollTop;
                        const hasScrolledPastHeader = scrollPos > elemHeight;
                        if (hasScrolledPastHeader !== isScrolled) {
                            setIsScrolled(hasScrolledPastHeader);
                        }
                    }
                }
            };

            checkScrollPosition();

            window.addEventListener('scroll', checkScrollPosition);

            return () => {
                window.removeEventListener('scroll', checkScrollPosition);
            };
        }
    }, [isScrolled, lg, isBrowser]);

    const dealerFees = useMemo(
        () => (!isEmpty(currentDeal?.extraFields?.dealerFees) ? JSON.parse(currentDeal?.extraFields?.dealerFees) : []),
        [currentDeal?.extraFields?.dealerFees]
    );

    const selectedTradeValue = !!currentDeal?.extraFields?.selectedPxValue;
    const selectedFeesChoice = currentDeal?.extraFields?.selectedFeesValue;

    const { isDealerFeesEnabled, isCarteGriseEnabled } = useFeatureSwitch();

    const scrollToSectionById = (sectionId: string) => {
        const element = document.getElementById(sectionId);

        if (element) {
            const rect = element.getBoundingClientRect();
            window.scrollTo({
                top: rect.top + window.pageYOffset - 10,
                behavior: 'smooth',
            });
        }
    };

    const { hasSelectedPaymentJourney = false } = currentDeal?.extraFields ?? {};

    const isPxEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_PX_ENABLED);
    const isLeasysEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_IS_LEASYS_ENABLED);
    const isScrappageEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_SHOW_SCRAPPAGE_MODAL);

    const hasMonthlyPrices = hasMonthlyPriceByType(currentDeal?.fullProductConfiguration?.prices, EMPLOYEE_PRICE_TYPE);

    const generateButtonElement = ({
        condition,
        iconCondition,
        onClickFunction,
        icon,
        title,
        ref,
        dataTestId,
    }: GenerateButtonElementProps) =>
        condition && (
            <button
                ref={ref}
                data-testid={dataTestId}
                className={`nav-item ${iconCondition ? 'selected' : ''}`}
                onClick={onClickFunction}
            >
                <span className={`icon ${iconCondition ? 'withIcon selected' : ''}`}>
                    {iconCondition && <Icon width={16} height={16} name={icon} />}
                </span>
                <span className="title">{t(title)}</span>
                {isBrandAC && <div className="selectedBorder" />}
            </button>
        );

    const elements: JSX.Element[] = [
        generateButtonElement({
            condition: !isRent && isScrappageEnabled && carDetails?.importInputs?.ecologicalBonusScrappage > 0,
            iconCondition: true,
            onClickFunction: () => scrollToSectionById(scrappageTemplateId),
            icon: Icons.Check,
            title: 'nav.steps.scrappage',
            ref: React.createRef(),
            dataTestId: 'TESTING_BASKET_STEP_SCRAPPAGE',
        }),
        generateButtonElement({
            condition: true,
            iconCondition: hasSelectedPaymentJourney || !hasMonthlyPrices,
            onClickFunction: () => scrollToSectionById(paymentTemplateId),
            icon: Icons.Check,
            title: 'nav.steps.payment',
            ref: React.createRef(),
            dataTestId: 'TESTING_BASKET_STEP_PAYMENT',
        }),
        generateButtonElement({
            condition: !isRent && isPxEnabled,
            iconCondition: selectedTradeValue,
            onClickFunction: () => scrollToSectionById(pxTemplateId),
            icon: Icons.Check,
            title: 'nav.steps.tradeIn',
            ref: React.createRef(),
            dataTestId: 'TESTING_BASKET_STEP_TRADE_IN',
        }),
        generateButtonElement({
            condition: !isRent && isCarteGriseEnabled,
            iconCondition: Boolean(selectedFeesChoice),
            onClickFunction: () => scrollToSectionById(registrationFeesTemplateId),
            icon: Icons.Check,
            title: 'nav.steps.administrationFees',
            ref: React.createRef(),
            dataTestId: 'TESTING_BASKET_STEP_CARTE_GRISE',
        }),
        generateButtonElement({
            condition: !isRent && isDealerFeesEnabled && hasMandatoryDealerFees(dealerFees),
            iconCondition: true,
            onClickFunction: () => scrollToSectionById(mandatoryFeesTemplateId),
            icon: Icons.Check,
            title: 'nav.steps.additionalCosts',
            ref: React.createRef(),
            dataTestId: 'TESTING_BASKET_STEP_MANDATORY_DEALER_FEES',
        }),
        generateButtonElement({
            condition: !isRent && isDealerFeesEnabled && hasOptionalDealerFees(dealerFees),
            iconCondition: hasSelectedOptionalDealerFees(currentDeal),
            onClickFunction: () => scrollToSectionById(optionalFeesTemplateId),
            icon: Icons.Check,
            title: 'nav.steps.additionalServices',
            ref: React.createRef(),
            dataTestId: 'TESTING_BASKET_STEP_OPTIONAL_DEALER_FEES',
        }),
        generateButtonElement({
            condition: !isRent && currentDeal?.fullProductConfiguration?.accessories?.length > 0,
            iconCondition: hasSelectedOptionalAccessories(currentDeal),
            onClickFunction: () => scrollToSectionById(optionalAccessoriesTemplateId),
            icon: Icons.Check,
            title: 'nav.steps.additionalAccessories',
            ref: React.createRef(),
            dataTestId: 'TESTING_BASKET_STEP_ACCESSORIES',
        }),
        generateButtonElement({
            condition: (!isRent && isUserLoggedInAsDealer) || !!currentDeal?.extraFields?.comments,
            iconCondition: !!currentDeal?.extraFields?.comments,
            onClickFunction: () => scrollToSectionById(commentTemplateId),
            icon: Icons.Check,
            title: 'nav.steps.comment',
            ref: React.createRef(),
            dataTestId: 'TESTING_BASKET_STEP_COMMENT',
        }),
        generateButtonElement({
            condition: isRent,
            iconCondition: false,
            onClickFunction: () => scrollToSectionById(userTemplateId),
            icon: Icons.Check,
            title: 'nav.steps.userDetails',
            ref: React.createRef(),
            dataTestId: 'TESTING_BASKET_STEP_USER',
        }),
    ].filter(Boolean);

    const nextPosition = useMemo(() => {
        const array = [];

        if (isBrowser) {
            const navItem = document.getElementsByClassName('nav-item');

            if (navItem.length) {
                for (let k = 0; k <= navItem.length; k++) {
                    array.push((navItem[k] as HTMLButtonElement)?.offsetWidth);
                }
            }

            return {
                sumAll: array.filter(Boolean).reduce((sum, item) => sum + (item + 30), 0),
            };
        }
        return {
            sumAll: 0,
        };
    }, [elements, isBrowser]);

    const calculateScrollableArea = useCallback(() => {
        if (isBrowser) {
            const widthBlurryHolder = [];

            const stepsContainer = document.getElementById('stepsContainer');
            const stepsWrapper = document.getElementById('stepsWrapper');
            const blurryHolder = document.getElementsByClassName('blurryHolder');

            for (let k = 0; k <= blurryHolder.length; k++) {
                if (blurryHolder[k]) {
                    widthBlurryHolder.push((blurryHolder[k] as HTMLDivElement).offsetWidth);
                }
            }

            const sumAllElementsWidth = nextPosition.sumAll;
            const sumBlurry = widthBlurryHolder.reduce((sum, item) => sum + item, 0);

            const sumScollAbleArea = stepsWrapper?.offsetWidth - sumBlurry;

            const isScrollAvailable = sumAllElementsWidth > stepsWrapper?.offsetWidth;

            return {
                sumAllElementsWidth,
                sumScollAbleArea,
                stepsContainer,
                stepsWrapper,
                blurryHolder,
                isScrollAvailable,
            };
        }
        return {
            sumAllElementsWidth: 0,
            sumScollAbleArea: 0,
            stepsContainer: null,
            stepsWrapper: null,
            blurryHolder: null,
            isScrollAvailable: false,
        };
    }, [nextPosition, elements, isBrowser]);

    calculateScrollableArea();

    const handleMove = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, pos: string) => {
        const { stepsContainer, stepsWrapper, sumAllElementsWidth } = calculateScrollableArea();

        if (pos === 'isLeft') {
            setBlockNext(false);
            stepsContainer.style.left = `0px`;
        } else {
            stepsWrapper.style.height = `${stepsContainer.offsetHeight}px`;
            stepsContainer.style.position = 'absolute';
            stepsContainer.style.left = `-${sumAllElementsWidth / elements.length}px`;

            setBlockNext(true);
        }
    };

    const Arrow = ({ pos }: { pos: string }) => {
        const { isScrollAvailable } = calculateScrollableArea();
        return isScrollAvailable ? (
            <div onClick={(e) => handleMove(e, pos)} className={`blurryHolder ${pos}`}>
                <Icon width={22} height={22} className="chevronRightIcon" name={Icons.ChevronRightThin} />
                <div className="blurry" />
            </div>
        ) : null;
    };

    const dispatch = useDispatch();

    return (
        <div className={className}>
            <div className={`${dropdown ? 'opened' : 'closed'}`}>
                <div id="stepsWrapper" className="wrapper">
                    <span className="project">{t('nav.steps.project')}</span>
                    {blockNext && <Arrow pos="isLeft" />}
                    <div id="stepsContainer" className="container">
                        {elements}
                    </div>
                    {!blockNext && <Arrow pos="isRight" />}
                </div>
                <div className="contactUsContainer">
                    <button
                        onClick={() => {
                            dispatch(
                                UIDuck.openModal({
                                    data: {},
                                    modalType: UIDuck.MODAL_TYPES.NEED_HELP,
                                    modalVersion: ModalVersion.v2,
                                })
                            );
                        }}
                        className="contactUsActionButton"
                    >
                        <Link label={t('header.nav.needHelp')} primary withArrowIcon />
                    </button>
                    {isScrolled && <span className="contactUsNumber">{t('support.telephone')}</span>}
                </div>
                <div className="chevron-down" onClick={() => setDropdown(!dropdown)}>
                    <Icon name={Icons.ChevronDown} width={12} height={12} />
                </div>
            </div>
        </div>
    );
};
